import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { adminApi } from '@vmgate/common';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../utils/format';
import { useEffect, useState } from 'react';

const profileSchema = z.object({
  full_name: z.string().min(1, 'Full name is required'),
  email: z.string().email('Valid email required')
});

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Current password required'),
    new_password: z.string().min(8, 'New password must be at least 8 characters'),
    confirm_password: z.string().min(8, 'Confirm password required')
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password']
  });

type ProfileFormValues = z.infer<typeof profileSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

export function ProfilePage() {
  const { user, refresh } = useAuth();
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: user?.full_name ?? '',
      email: user?.email ?? ''
    }
  });
  useEffect(() => {
    reset({
      full_name: user?.full_name ?? '',
      email: user?.email ?? ''
    });
  }, [reset, user?.email, user?.full_name]);

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: passwordSubmitting },
    reset: resetPasswordForm
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema)
  });

  const profileMutation = useMutation({
    mutationFn: adminApi.updateProfile,
    onSuccess: () => {
      refresh();
    }
  });

  const passwordMutation = useMutation({
    mutationFn: adminApi.changePassword,
    onSuccess: () => {
      setPasswordSuccess('Password updated successfully.');
      resetPasswordForm();
    }
  });

  if (!user) {
    return null;
  }

  const onSubmitProfile = async (values: ProfileFormValues) => {
    await profileMutation.mutateAsync(values);
  };

  const onSubmitPassword = async (values: PasswordFormValues) => {
    setPasswordSuccess(null);
    await passwordMutation.mutateAsync({
      current_password: values.current_password,
      new_password: values.new_password
    });
  };

  return (
    <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: '1.2fr 1fr', alignItems: 'start' }}>
      <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Profile</h2>
          <div className="text-muted">Update your account information.</div>
        </div>

        <form onSubmit={handleSubmit(onSubmitProfile)} style={{ display: 'grid', gap: '1rem', maxWidth: '420px' }}>
          <FormField label="Full name" error={errors.full_name?.message}>
            <input type="text" {...register('full_name')} style={inputStyle} />
          </FormField>
          <FormField label="Email" error={errors.email?.message}>
            <input type="email" {...register('email')} style={inputStyle} />
          </FormField>
          <button className="btn btn-primary" type="submit" disabled={isSubmitting || profileMutation.isPending}>
            {profileMutation.isPending ? 'Saving...' : 'Save changes'}
          </button>
          {profileMutation.isSuccess && !profileMutation.isPending && (
            <div style={{ color: '#047857', fontSize: '0.9rem' }}>Profile updated.</div>
          )}
        </form>
      </div>

      <div className="surface surface-padding" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>Security</h3>
          <div className="text-muted">Change your password regularly to keep the account secure.</div>
        </div>
        <form onSubmit={handleSubmitPassword(onSubmitPassword)} style={{ display: 'grid', gap: '1rem' }}>
          <FormField label="Current password" error={passwordErrors.current_password?.message}>
            <input type="password" {...registerPassword('current_password')} style={inputStyle} />
          </FormField>
          <FormField label="New password" error={passwordErrors.new_password?.message}>
            <input type="password" {...registerPassword('new_password')} style={inputStyle} />
          </FormField>
          <FormField label="Confirm new password" error={passwordErrors.confirm_password?.message}>
            <input type="password" {...registerPassword('confirm_password')} style={inputStyle} />
          </FormField>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={passwordSubmitting || passwordMutation.isPending}
          >
            {passwordMutation.isPending ? 'Updating...' : 'Update password'}
          </button>
          {passwordSuccess && <div style={{ color: '#047857', fontSize: '0.9rem' }}>{passwordSuccess}</div>}
          {passwordMutation.isError && (
            <div style={{ color: 'var(--color-danger)', fontSize: '0.9rem' }}>
              Unable to change password. Please verify your current password.
            </div>
          )}
        </form>
      </div>

      <div className="surface surface-padding" style={{ gridColumn: '1 / -1' }}>
        <h3 style={{ marginTop: 0 }}>Account metadata</h3>
        <table className="data-table">
          <tbody>
            <tr>
              <th style={{ width: '30%' }}>Username</th>
              <td>{user.username}</td>
            </tr>
            <tr>
              <th>Role</th>
              <td>{user.role}</td>
            </tr>
            <tr>
              <th>Status</th>
              <td>{user.status}</td>
            </tr>
            <tr>
              <th>Created</th>
              <td>{formatDateTime(user.created_at)}</td>
            </tr>
            <tr>
              <th>Last login</th>
              <td>{formatDateTime(user.last_login)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>{error}</span>}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  padding: '0.65rem 0.85rem',
  fontSize: '0.95rem',
  background: '#fff'
};

